import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCenter,
  forceCollide,
} from "d3";
import ELK from "elkjs/lib/elk-api.js";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { type Edge, type Node } from "reactflow";
import { computeNodeScale } from "../../../../../componentes/easyMaps/elementNode";
import { createElkWorker } from "./elkWorker";

// Organização automática do mapa de topologia.
//
// - Hierárquico: ELK "layered", rodando num Web Worker (não trava a tela).
//   As caixas dos enlaces entram como rótulos do ELK, que reserva espaço
//   para elas e devolve onde ficaram.
// - Físico: d3-force (Barnes–Hut, O(n log n)). Cada caixa de enlace vira um
//   nó no meio do enlace (card → caixa → card), então a simulação já afasta
//   caixas de cards e de outras caixas. No fim, uma varredura remove
//   qualquer sobreposição de retângulos que tenha sobrado.
//
// Nos dois casos a posição da caixa é devolvida como `labelT`: fração (0–1)
// do comprimento da linha onde a caixa fica. O PositionableEdge desenha a
// caixa nesse ponto (sem `labelT`, no meio, como antes) e o valor é salvo no
// config do enlace. Por ser uma fração, a caixa acompanha a linha quando um
// card é movido depois.

const NODE_W = 160;
const NODE_H = 60;
// Folga entre retângulos (cards e caixas)
const GAP = 12;
// Afastamento da linha ao sair do card antes da primeira dobra
const OUT_GAP = 14;

type Direction = "TB" | "BT" | "LR" | "RL";
type Algo = "elk" | "fd";
type Pos = { x: number; y: number };
type Size = { w: number; h: number };
type Handler = { x: number; y: number; active: number };

type EdgeOverride = {
  sourceHandle?: string | null;
  targetHandle?: string | null;
  lineType: string;
  positionHandlers?: Handler[];
  labelT?: number;
};

type LayoutSnapshot = {
  nodes: { id: string; position: Pos }[];
  edges: {
    id: string;
    positionHandlers: any[];
    lineType?: string;
    sourceHandle?: string | null;
    targetHandle?: string | null;
    labelT?: number;
  }[];
};

type LayoutResult = {
  positions: Map<string, Pos>; // canto superior esquerdo, como o React Flow
  edgeOverrides: Map<string, EdgeOverride>;
};

// Tamanho aproximado do card por tipo de visualização (escala 1), usado só
// enquanto o React Flow ainda não mediu o nó (elementNode/styles.ts)
const NODE_SIZE_BY_VIEW: Record<string, Size> = {
  detailed: { w: 240, h: 80 },
  simple: { w: 130, h: 44 },
  compact: { w: 56, h: 56 },
};
// Idem para a caixa do enlace, enquanto não está no DOM
const LABEL_SIZE_BY_VIEW: Record<string, Size> = {
  detailed: { w: 150, h: 56 },
  simple: { w: 110, h: 30 },
  compact: { w: 90, h: 24 },
};

const HANDLE_MAP: Record<Direction, { src: string; tgt: string }> = {
  TB: { src: "bottom", tgt: "top" },
  BT: { src: "top", tgt: "bottom" },
  LR: { src: "right", tgt: "left" },
  RL: { src: "left", tgt: "right" },
};

const OPPOSITE: Record<string, string> = {
  right: "left",
  left: "right",
  bottom: "top",
  top: "bottom",
};

// --- ELK em Web Worker ---

// Uma instância para o app inteiro. O worker roda o ELK fora da thread da
// interface: mapas grandes levam segundos para organizar e a tela continua
// respondendo. Se o navegador não conseguir criar o worker, cai no bundle
// síncrono (mesmo resultado, só que na thread principal).
let elkInstance: any = null;
async function getElk(): Promise<any> {
  if (elkInstance) return elkInstance;
  try {
    elkInstance = new (ELK as any)({
      workerFactory: createElkWorker,
    });
  } catch {
    const { default: ElkBundled } = await import("elkjs/lib/elk.bundled.js");
    elkInstance = new (ElkBundled as any)();
  }
  return elkInstance;
}

// --- geometria ---

function sideFromDelta(dx: number, dy: number): string {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? "right" : "left";
  return dy >= 0 ? "bottom" : "top";
}

// Ponto do handle do React Flow (centro do lado do card)
function anchorOf(pos: Pos, size: Size, side: string): Pos {
  return {
    x:
      side === "left"
        ? pos.x
        : side === "right"
          ? pos.x + size.w
          : pos.x + size.w / 2,
    y:
      side === "top"
        ? pos.y
        : side === "bottom"
          ? pos.y + size.h
          : pos.y + size.h / 2,
  };
}

// Lado do card mais próximo do ponto (o ângulo a partir do centro erra em
// cards largos: um ponto na borda de baixo perto do canto virava "right")
function nearestSide(pt: Pos, pos: Pos, size: Size): string {
  const d: [string, number][] = [
    ["top", Math.abs(pt.y - pos.y)],
    ["bottom", Math.abs(pt.y - (pos.y + size.h))],
    ["left", Math.abs(pt.x - pos.x)],
    ["right", Math.abs(pt.x - (pos.x + size.w))],
  ];
  return d.reduce((a, b) => (b[1] < a[1] ? b : a))[0];
}

// Fração do comprimento da polilinha no ponto dela mais próximo de `p`
function fractionAlong(pts: Pos[], p: Pos): number {
  let total = 0;
  let bestAt = 0;
  let bestDist = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const len = Math.sqrt(len2);
    const r = len2
      ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2))
      : 0;
    const d = Math.hypot(a.x + dx * r - p.x, a.y + dy * r - p.y);
    if (d < bestDist) {
      bestDist = d;
      bestAt = total + len * r;
    }
    total += len;
  }
  return total ? bestAt / total : 0.5;
}

// Remove handlers duplicados ou no meio de um trecho reto: o desenho não
// muda, mas sobram menos pontos para arrastar na edição manual
function dropCollinear(start: Pos, handlers: Handler[], end: Pos): Handler[] {
  const out: Handler[] = [];
  let prev = start;
  handlers.forEach((h, i) => {
    const next = handlers[i + 1] ?? end;
    const cross =
      (h.x - prev.x) * (next.y - h.y) - (h.y - prev.y) * (next.x - h.x);
    if (Math.hypot(h.x - prev.x, h.y - prev.y) < 0.5 || Math.abs(cross) < 0.5)
      return;
    out.push(h);
    prev = h;
  });
  return out;
}

function segmentsIntersect(a: Pos, b: Pos, c: Pos, d: Pos): boolean {
  const d1x = b.x - a.x;
  const d1y = b.y - a.y;
  const d2x = d.x - c.x;
  const d2y = d.y - c.y;
  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return false;
  const t = ((c.x - a.x) * d2y - (c.y - a.y) * d2x) / cross;
  const u = ((c.x - a.x) * d1y - (c.y - a.y) * d1x) / cross;
  return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
}

// Cruzamentos entre polilinhas (usado só para escolher entre sementes)
function countCrossings(lines: Pos[][]): number {
  let count = 0;
  for (let i = 0; i < lines.length; i++)
    for (let j = i + 1; j < lines.length; j++)
      for (let a = 0; a < lines[i].length - 1; a++)
        for (let b = 0; b < lines[j].length - 1; b++)
          if (
            segmentsIntersect(
              lines[i][a],
              lines[i][a + 1],
              lines[j][b],
              lines[j][b + 1],
            )
          )
            count++;
  return count;
}

type Box = { id: string; x: number; y: number; w: number; h: number };

// Remove sobreposições entre retângulos (x/y = centro) por varredura no eixo
// x (sweep and prune): ordena pela borda esquerda e só compara com quem
// ainda está no alcance — O(n log n + pares próximos), em vez de todos
// contra todos. Cada par sobreposto é afastado pelo eixo de menor
// penetração, metade para cada lado.
function separateBoxes(boxes: Box[], gap: number, maxIter = 80): void {
  for (let iter = 0; iter < maxIter; iter++) {
    boxes.sort((a, b) => a.x - a.w / 2 - (b.x - b.w / 2));
    let moved = false;
    for (let i = 0; i < boxes.length; i++) {
      const a = boxes[i];
      const right = a.x + a.w / 2 + gap;
      for (let j = i + 1; j < boxes.length; j++) {
        const b = boxes[j];
        if (b.x - b.w / 2 >= right) break;
        const px = (a.w + b.w) / 2 + gap - Math.abs(b.x - a.x);
        const py = (a.h + b.h) / 2 + gap - Math.abs(b.y - a.y);
        if (px <= 0 || py <= 0) continue;
        moved = true;
        if (px < py) {
          const s = b.x >= a.x ? 1 : -1;
          a.x -= (s * px) / 2;
          b.x += (s * px) / 2;
        } else {
          const s = b.y >= a.y ? 1 : -1;
          a.y -= (s * py) / 2;
          b.y += (s * py) / 2;
        }
      }
    }
    if (!moved) break;
  }
}

// --- tamanhos ---

// Tamanho real medido pelo React Flow; antes da 1ª medição, estimado pelo
// tipo de visualização do card × escala ("size" do elemento)
function nodeSize(n: Node): Size {
  if (n.width && n.height) return { w: n.width, h: n.height };
  const base = NODE_SIZE_BY_VIEW[(n.data as any)?.viewType] ?? {
    w: NODE_W,
    h: NODE_H,
  };
  const scale = computeNodeScale((n.data as any)?.size);
  return { w: base.w * scale, h: base.h * scale };
}

// Tamanho de cada caixa de enlace visível, em unidades do fluxo. Uma única
// consulta ao DOM: o PositionableEdge marca a caixa (no modo de edição, a
// caixa vazia) com data-edge-label. offsetWidth ignora o zoom do viewport.
function measureEdgeLabels(edges: Edge[]): Map<string, Size> {
  const measured = new Map<string, Size>();
  document.querySelectorAll<HTMLElement>("[data-edge-label]").forEach((el) => {
    if (el.offsetWidth)
      measured.set(el.dataset.edgeLabel!, {
        w: el.offsetWidth,
        h: el.offsetHeight,
      });
  });
  const sizes = new Map<string, Size>();
  for (const e of edges) {
    const d = e.data as any;
    if (!d?.showLabelEnlace) continue;
    sizes.set(
      e.id,
      measured.get(e.id) ??
        LABEL_SIZE_BY_VIEW[d?.viewType] ??
        LABEL_SIZE_BY_VIEW.detailed,
    );
  }
  return sizes;
}

// --- hook ---

export function useAutoLayout(
  nodes: Node[],
  edges: Edge[],
  setNodes: Dispatch<SetStateAction<Node[]>>,
  setEdges: Dispatch<SetStateAction<Edge[]>>,
  fitView: (options?: { padding?: number; duration?: number }) => void,
  setCheckChanges?: Dispatch<SetStateAction<boolean>>,
) {
  const [preLayoutSnapshot, setPreLayoutSnapshot] =
    useState<LayoutSnapshot | null>(null);
  const layoutPositionsRef = useRef<Map<string, Pos>>(new Map());
  const layoutEdgesRef = useRef<Map<string, EdgeOverride>>(new Map());
  // Cálculos assíncronos (worker): arrastar o slider dispara vários e só o
  // último pode ser aplicado
  const runIdRef = useRef(0);
  const lastRunRef = useRef<
    [Algo, Direction | null, number, number, number, number] | null
  >(null);

  const saveSnapshot = () => {
    if (preLayoutSnapshot) return;
    setPreLayoutSnapshot({
      nodes: nodes.map((n) => ({ id: n.id, position: { ...n.position } })),
      edges: edges.map((e) => ({
        id: e.id,
        positionHandlers: structuredClone(
          (e.data as any)?.positionHandlers ?? [],
        ),
        lineType: (e.data as any)?.lineType,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        labelT: (e.data as any)?.labelT,
      })),
    });
  };

  // Dados de entrada comuns aos dois algoritmos
  const prepare = () => {
    const sizeMap = new Map<string, Size>(
      nodes.map((n) => [n.id, nodeSize(n)]),
    );
    const sizeOf = (id: string) => sizeMap.get(id) ?? { w: NODE_W, h: NODE_H };
    const ids = new Set(nodes.map((n) => n.id));
    const validEdges = edges.filter(
      (e) => e.source !== e.target && ids.has(e.source) && ids.has(e.target),
    );
    return {
      sizeOf,
      validEdges,
      labelSizes: measureEdgeLabels(validEdges),
    };
  };

  // ---------------- Hierárquico (ELK layered) ----------------

  const computeElkLayout = async (
    dir: Direction,
    nodesep: number,
    ranksep: number,
    edgesep: number,
    portsep: number,
  ): Promise<LayoutResult> => {
    const { sizeOf, validEdges, labelSizes } = prepare();
    const vertical = dir === "TB" || dir === "BT";

    // Com caixas, o ELK cria uma camada só para elas e aplica o espaçamento
    // entre níveis DUAS vezes (card→caixa e caixa→card). Dividimos para que
    // o vão entre cards continue sendo o `ranksep` escolhido, com a caixa
    // dentro dele — só cresce quando a caixa não cabe.
    let labelThick = 0;
    labelSizes.forEach((l) => {
      labelThick = Math.max(labelThick, vertical ? l.h : l.w);
    });
    const layerGap = labelThick
      ? Math.max(OUT_GAP + GAP, (ranksep - labelThick) / 2)
      : ranksep;

    const graph = {
      id: "root",
      layoutOptions: {
        "elk.algorithm": "layered",
        "elk.direction": { TB: "DOWN", BT: "UP", LR: "RIGHT", RL: "LEFT" }[dir],
        "elk.edgeRouting": "ORTHOGONAL",
        "elk.spacing.nodeNode": String(nodesep),
        "elk.layered.spacing.nodeNodeBetweenLayers": String(layerGap),
        "elk.layered.spacing.edgeEdgeBetweenLayers": String(edgesep),
        "elk.spacing.edgeEdge": String(edgesep),
        // Afasta enlaces de cards que não participam deles
        "elk.spacing.edgeNode": String(Math.max(edgesep * 2, 20)),
        "elk.spacing.edgeLabel": String(GAP / 2),
        // FREE: o ELK distribui os enlaces pelo lado do card
        portConstraints: "FREE",
        // Mais varreduras de cruzamento custam tempo: só em mapas menores
        "elk.layered.thoroughness": nodes.length <= 150 ? "20" : "7",
        "elk.layered.crossingMinimization.greedySwitch.type": "TWO_SIDED",
        // Linhas mais retas (minimiza dobras/comprimento)
        "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
        // Componentes desconexos lado a lado, em blocos compactos
        "elk.separateConnectedComponents": "true",
        "elk.spacing.componentComponent": String(nodesep * 2),
        // Empates seguem a ordem do modelo: resultado estável entre execuções
        "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
      },
      children: nodes.map((n) => ({
        id: n.id,
        width: sizeOf(n.id).w,
        height: sizeOf(n.id).h,
      })),
      edges: validEdges.map((e) => {
        const ls = labelSizes.get(e.id);
        return {
          id: e.id,
          sources: [e.source],
          targets: [e.target],
          // A caixa no meio do enlace, atravessada por ele. As opções
          // precisam ser por rótulo e o rótulo precisa de `text`, senão o
          // ELK ignora o tamanho e não reserva espaço.
          ...(ls && {
            labels: [
              {
                id: `${e.id}__label`,
                text: " ",
                width: ls.w,
                height: ls.h,
                layoutOptions: {
                  "elk.edgeLabels.placement": "CENTER",
                  "elk.edgeLabels.inline": "true",
                },
              },
            ],
          }),
        };
      }),
    };

    const layout = await (await getElk()).layout(graph);

    const posMap = new Map<string, Pos>(
      (layout.children ?? []).map((n: any) => [
        n.id,
        { x: n.x ?? 0, y: n.y ?? 0 },
      ]),
    );
    const elkEdges = new Map<string, any>(
      (layout.edges ?? []).map((e: any) => [e.id, e]),
    );
    const sideOf = (pt: Pos, id: string) =>
      nearestSide(pt, posMap.get(id)!, sizeOf(id));

    // --- Reespaçamento das saídas ---
    // O ELK espalha as portas pelo lado do card com espaçamento próprio.
    // Reagrupamos por (card, lado), mantemos a ordem do ELK (não cria
    // cruzamentos) e redistribuímos com o passo `portsep`, centrado no lado
    // e sem passar da borda (cards compactos são estreitos).
    // Só sem caixas: com caixas, o ELK já afasta as saídas pela largura delas,
    // e aproximar os trechos arrastaria as caixas umas sobre as outras.
    const exitGroups = new Map<string, { key: string; along: number }[]>();
    for (const e of labelSizes.size ? [] : validEdges) {
      const sec = elkEdges.get(e.id)?.sections?.[0];
      if (!sec) continue;
      for (const [pt, nodeId, end] of [
        [sec.startPoint, e.source, "start"],
        [sec.endPoint, e.target, "end"],
      ] as [Pos, string, string][]) {
        const side = sideOf(pt, nodeId);
        const group = `${nodeId}#${side}`;
        if (!exitGroups.has(group)) exitGroups.set(group, []);
        exitGroups.get(group)!.push({
          key: `${e.id}#${end}`,
          along: side === "top" || side === "bottom" ? pt.x : pt.y,
        });
      }
    }
    const exitPoint = new Map<string, Pos>();
    for (const [group, refs] of exitGroups) {
      const cut = group.lastIndexOf("#");
      const nodeId = group.slice(0, cut);
      const side = group.slice(cut + 1);
      const pos = posMap.get(nodeId)!;
      const size = sizeOf(nodeId);
      const horizontal = side === "top" || side === "bottom";
      refs.sort((a, b) => a.along - b.along);
      const span = Math.max(0, (horizontal ? size.w : size.h) - 8);
      const step =
        refs.length > 1 ? Math.min(portsep, span / (refs.length - 1)) : 0;
      const center = horizontal ? pos.x + size.w / 2 : pos.y + size.h / 2;
      const border = anchorOf(pos, size, side);
      refs.forEach((r, i) => {
        const along = center + (i - (refs.length - 1) / 2) * step;
        exitPoint.set(
          r.key,
          horizontal ? { x: along, y: border.y } : { x: border.x, y: along },
        );
      });
    }

    const edgeOverrides = new Map<string, EdgeOverride>();
    const handles = HANDLE_MAP[dir];
    for (const e of validEdges) {
      const elkEdge = elkEdges.get(e.id);
      const sec = elkEdge?.sections?.[0];
      const srcSide = sec ? sideOf(sec.startPoint, e.source) : handles.src;
      const tgtSide = sec ? sideOf(sec.endPoint, e.target) : handles.tgt;
      const srcAnchor = anchorOf(
        posMap.get(e.source)!,
        sizeOf(e.source),
        srcSide,
      );
      const tgtAnchor = anchorOf(
        posMap.get(e.target)!,
        sizeOf(e.target),
        tgtSide,
      );

      const bends: Pos[] = (sec?.bendPoints ?? []).map((p: any) => ({
        x: p.x,
        y: p.y,
      }));
      // O reespaçamento da porta propaga para o trecho reto que sai dela
      // (bendPoints alinhados com a porta original acompanham o deslocamento)
      const shiftRun = (
        fromStart: boolean,
        orig: Pos | undefined,
        moved: Pos | undefined,
        side: string,
      ) => {
        if (!orig || !moved) return;
        const axis: "x" | "y" = side === "top" || side === "bottom" ? "x" : "y";
        const delta = moved[axis] - orig[axis];
        if (Math.abs(delta) < 0.5) return;
        for (let k = 0; k < bends.length; k++) {
          const i = fromStart ? k : bends.length - 1 - k;
          if (Math.abs(bends[i][axis] - orig[axis]) > 1) break;
          bends[i][axis] += delta;
        }
      };
      const startPt: Pos | undefined =
        exitPoint.get(`${e.id}#start`) ?? sec?.startPoint;
      const endPt: Pos | undefined =
        exitPoint.get(`${e.id}#end`) ?? sec?.endPoint;
      shiftRun(true, sec?.startPoint, startPt, srcSide);
      shiftRun(false, sec?.endPoint, endPt, tgtSide);

      // Saída/chegada fora do centro do lado: o React Flow sai do handle
      // central, então o ponto de porta vira o 1º/último handler (empurrado
      // para fora do card, sem passar do próximo ponto da rota — senão a
      // linha iria além da dobra e voltaria)
      const outward = (pt: Pos, side: string, next: Pos): Handler => {
        const vert = side === "top" || side === "bottom";
        const gap = Math.min(
          OUT_GAP,
          Math.abs(vert ? next.y - pt.y : next.x - pt.x),
        );
        return {
          x: pt.x + (side === "left" ? -gap : side === "right" ? gap : 0),
          y: pt.y + (side === "top" ? -gap : side === "bottom" ? gap : 0),
          active: 0,
        };
      };
      const raw: Handler[] = [];
      if (
        startPt &&
        Math.hypot(startPt.x - srcAnchor.x, startPt.y - srcAnchor.y) > 4
      )
        raw.push(outward(startPt, srcSide, bends[0] ?? endPt ?? tgtAnchor));
      bends.forEach((p) => raw.push({ ...p, active: 0 }));
      if (endPt && Math.hypot(endPt.x - tgtAnchor.x, endPt.y - tgtAnchor.y) > 4)
        raw.push(
          outward(
            endPt,
            tgtSide,
            bends[bends.length - 1] ?? startPt ?? srcAnchor,
          ),
        );
      const handlers = dropCollinear(srcAnchor, raw, tgtAnchor);

      // Onde o ELK pôs a caixa → fração do comprimento da linha final
      const lbl = elkEdge?.labels?.[0];
      const labelT =
        lbl?.x != null && labelSizes.has(e.id)
          ? fractionAlong([srcAnchor, ...handlers, tgtAnchor], {
              x: lbl.x + lbl.width / 2,
              y: lbl.y + lbl.height / 2,
            })
          : undefined;

      edgeOverrides.set(e.id, {
        sourceHandle: srcSide,
        targetHandle: tgtSide,
        lineType: "reta",
        positionHandlers: handlers,
        labelT,
      });
    }

    return { positions: posMap, edgeOverrides };
  };

  // ---------------- Físico (d3-force) ----------------

  const computeForceLayout = (
    linkDist: number,
    repulsion: number,
  ): LayoutResult => {
    const { sizeOf, validEdges, labelSizes } = prepare();

    // Enlaces paralelos (mesmo par de cards)
    const pairKey = (e: Edge) => [e.source, e.target].sort().join("|");
    const pairCount = new Map<string, number>();
    validEdges.forEach((e) =>
      pairCount.set(pairKey(e), (pairCount.get(pairKey(e)) ?? 0) + 1),
    );

    // Cada enlace com caixa (ou paralelo, para abrir o leque) ganha um nó
    // no meio: card → meio → card. Com caixa, o nó tem o tamanho real dela,
    // então cards e caixas se afastam naturalmente na simulação.
    const midSize = (e: Edge): Size | null =>
      labelSizes.get(e.id) ??
      ((pairCount.get(pairKey(e)) ?? 0) > 1 ? { w: 16, h: 16 } : null);
    const midId = (e: Edge) => `${e.id}::mid`;

    type SimNode = Box;
    const degree = new Map<string, number>();
    validEdges.forEach((e) => {
      degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
      degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
    });
    const connected = nodes.filter((n) => degree.has(n.id));
    const isolated = nodes.filter((n) => !degree.has(n.id));

    // Meia extensão média do retângulo (o enlace sai em qualquer ângulo)
    const ext = (s: Size) => (s.w + s.h) / 4;

    // Distância por trecho: cobre os dois retângulos + folga; o slider é o
    // mínimo. Força menor em cards de grau alto (hubs não "puxam" tudo).
    type Link = {
      source: string;
      target: string;
      distance: number;
      strength: number;
    };
    const links: Link[] = [];
    for (const e of validEdges) {
      const ms = midSize(e);
      const s = sizeOf(e.source);
      const t = sizeOf(e.target);
      const strength =
        1 / Math.sqrt(Math.max(degree.get(e.source)!, degree.get(e.target)!));
      if (ms) {
        links.push(
          {
            source: e.source,
            target: midId(e),
            distance: Math.max(linkDist / 2, ext(s) + ext(ms) + GAP),
            strength,
          },
          {
            source: midId(e),
            target: e.target,
            distance: Math.max(linkDist / 2, ext(t) + ext(ms) + GAP),
            strength,
          },
        );
      } else {
        links.push({
          source: e.source,
          target: e.target,
          distance: Math.max(linkDist, ext(s) + ext(t) + GAP),
          strength,
        });
      }
    }

    // Semente radial: anéis por BFS a partir do card de maior grau (árvore
    // quase sem cruzamentos), cada componente ao lado da anterior
    const radialSeed = (): Map<string, Pos> => {
      const nb = new Map<string, string[]>();
      validEdges.forEach((e) => {
        if (!nb.has(e.source)) nb.set(e.source, []);
        if (!nb.has(e.target)) nb.set(e.target, []);
        nb.get(e.source)!.push(e.target);
        nb.get(e.target)!.push(e.source);
      });
      const order = [...connected].sort(
        (a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0),
      );
      const pos = new Map<string, Pos>();
      const seen = new Set<string>();
      let offsetX = 0;
      for (const root of order) {
        if (seen.has(root.id)) continue;
        seen.add(root.id);
        let level = [root.id];
        let li = 0;
        let maxR = 0;
        while (level.length) {
          const r = li * linkDist * 1.2;
          maxR = r;
          const next: string[] = [];
          for (let i = 0; i < level.length; i++) {
            const a = (i / level.length) * 2 * Math.PI + li * 0.5;
            pos.set(level[i], {
              x: offsetX + r * Math.cos(a),
              y: r * Math.sin(a),
            });
            for (const v of nb.get(level[i]) ?? []) {
              if (seen.has(v)) continue;
              seen.add(v);
              next.push(v);
            }
          }
          level = next;
          li++;
        }
        offsetX += 2 * maxR + linkDist * 2;
      }
      return pos;
    };
    const currentSeed = new Map<string, Pos>(
      connected.map((n) => {
        const s = sizeOf(n.id);
        return [n.id, { x: n.position.x + s.w / 2, y: n.position.y + s.h / 2 }];
      }),
    );

    const simulate = (seed: Map<string, Pos>): Map<string, SimNode> => {
      const simNodes: SimNode[] = connected.map((n) => ({
        id: n.id,
        ...seed.get(n.id)!,
        ...sizeOf(n.id),
      }));
      validEdges.forEach((e, i) => {
        const ms = midSize(e);
        if (!ms) return;
        const a = seed.get(e.source)!;
        const b = seed.get(e.target)!;
        // nasce no meio, com um leve desvio para paralelos não empilharem
        simNodes.push({
          id: midId(e),
          x: (a.x + b.x) / 2 + ((i % 5) - 2) * 7,
          y: (a.y + b.y) / 2 + ((i % 3) - 1) * 7,
          ...ms,
        });
      });
      const sim = forceSimulation(simNodes as any)
        .force(
          "link",
          forceLink(links.map((l) => ({ ...l })) as any)
            .id((d: any) => d.id)
            .distance((l: any) => l.distance)
            .strength((l: any) => l.strength),
        )
        .force(
          "charge",
          forceManyBody()
            .strength(-Math.max(repulsion * 3, linkDist * 4))
            .distanceMax(linkDist * 6),
        )
        // Círculo que cobre o retângulo (meia diagonal): aproximação rápida;
        // a sobreposição exata de retângulos é resolvida no fim
        .force(
          "collide",
          forceCollide(
            (d: any) => Math.hypot(d.w, d.h) / 2 + GAP / 2,
          ).iterations(1),
        )
        .force("center", forceCenter(0, 0))
        .stop();
      // ~300 ticks: até alpha < alphaMin com o decaimento padrão do d3
      const ticks = Math.ceil(
        Math.log(sim.alphaMin()) / Math.log(1 - sim.alphaDecay()),
      );
      for (let i = 0; i < ticks; i++) sim.tick();
      return new Map(simNodes.map((n) => [n.id, n]));
    };

    // Mapas pequenos/médios: duas sementes, fica a de menos cruzamentos.
    // Mapas grandes: só a radial (contar cruzamentos é O(E²)).
    const lineOf = (m: Map<string, SimNode>, e: Edge): Pos[] => {
      const mid = m.get(midId(e));
      return mid
        ? [m.get(e.source)!, mid, m.get(e.target)!]
        : [m.get(e.source)!, m.get(e.target)!];
    };
    let best = simulate(radialSeed());
    if (validEdges.length <= 200 && connected.length > 2) {
      const alt = simulate(currentSeed);
      const crossings = (m: Map<string, SimNode>) =>
        countCrossings(validEdges.map((e) => lineOf(m, e)));
      if (crossings(alt) < crossings(best)) best = alt;
    }

    // Cards isolados: em fileira abaixo do grafo
    let bottom = -Infinity;
    let left = Infinity;
    best.forEach((n) => {
      bottom = Math.max(bottom, n.y + n.h / 2);
      left = Math.min(left, n.x - n.w / 2);
    });
    if (!isFinite(bottom)) {
      bottom = 0;
      left = 0;
    }
    let cursor = left;
    isolated.forEach((n) => {
      const s = sizeOf(n.id);
      best.set(n.id, {
        id: n.id,
        x: cursor + s.w / 2,
        y: bottom + linkDist / 2 + s.h / 2,
        ...s,
      });
      cursor += s.w + GAP * 2;
    });

    // Sobreposição exata zero entre cards e caixas
    separateBoxes([...best.values()], GAP);

    const positions = new Map<string, Pos>();
    nodes.forEach((n) => {
      const b = best.get(n.id);
      if (b) positions.set(n.id, { x: b.x - b.w / 2, y: b.y - b.h / 2 });
    });

    const edgeOverrides = new Map<string, EdgeOverride>();
    for (const e of validEdges) {
      const s = best.get(e.source)!;
      const t = best.get(e.target)!;
      const mid = best.get(midId(e));
      // cada ponta sai pelo lado voltado para o meio (ou para o outro card)
      const srcSide = sideFromDelta((mid ?? t).x - s.x, (mid ?? t).y - s.y);
      const tgtSide = mid
        ? sideFromDelta(mid.x - t.x, mid.y - t.y)
        : OPPOSITE[srcSide];
      const srcAnchor = anchorOf(
        positions.get(e.source)!,
        sizeOf(e.source),
        srcSide,
      );
      const tgtAnchor = anchorOf(
        positions.get(e.target)!,
        sizeOf(e.target),
        tgtSide,
      );
      const handlers: Handler[] = mid
        ? [{ x: mid.x, y: mid.y, active: 0 }]
        : [];
      edgeOverrides.set(e.id, {
        sourceHandle: srcSide,
        targetHandle: tgtSide,
        lineType: "reta",
        positionHandlers: handlers,
        // a caixa fica exatamente no handler do meio
        labelT:
          mid && labelSizes.has(e.id)
            ? fractionAlong([srcAnchor, mid, tgtAnchor], mid)
            : undefined,
      });
    }

    return { positions, edgeOverrides };
  };

  // Aplica o resultado nos nós/enlaces e guarda para o "Salvar"
  const applyResult = ({ positions, edgeOverrides }: LayoutResult) => {
    setNodes((prev) =>
      prev.map((node) => {
        const position = positions.get(node.id);
        return position ? { ...node, position } : node;
      }),
    );
    setEdges((prev) =>
      prev.map((edge) => {
        const ov = edgeOverrides.get(edge.id);
        if (!ov || !edge.data) return edge;
        return {
          ...edge,
          sourceHandle: ov.sourceHandle,
          targetHandle: ov.targetHandle,
          data: {
            ...edge.data,
            positionHandlers: ov.positionHandlers,
            lineType: ov.lineType,
            labelT: ov.labelT,
          },
        };
      }),
    );
    layoutPositionsRef.current = positions;
    layoutEdgesRef.current = edgeOverrides;
  };

  const handleAutoLayout = async (
    algo: Algo = "elk",
    dir: Direction | null = "TB",
    nodesep: number = 80,
    ranksep: number = 120,
    edgesep: number = 10,
    portsep: number = 20,
  ) => {
    saveSnapshot();
    lastRunRef.current = [algo, dir, nodesep, ranksep, edgesep, portsep];
    const runId = ++runIdRef.current;
    let result: LayoutResult;
    try {
      result =
        algo === "fd"
          ? computeForceLayout(nodesep * 2, ranksep)
          : await computeElkLayout(
              (dir ?? "TB") as Direction,
              nodesep,
              ranksep,
              edgesep,
              portsep,
            );
    } catch (err) {
      console.error("Falha ao organizar o layout", err);
      return;
    }
    if (runId !== runIdRef.current) return;
    applyResult(result);
    setCheckChanges?.(true);
    setTimeout(() => fitView({ padding: 0.12, duration: 350 }), 60);
  };

  // Mudou a visualização (compacto/simples/detalhado) ou o tamanho dos cards
  // ou das caixas com o layout aberto: as posições foram calculadas para os
  // tamanhos antigos — refaz com os mesmos parâmetros. O atraso deixa o
  // React Flow remedir os cards e o DOM refletir as caixas novas.
  const sizeKey = useMemo(
    () =>
      nodes
        .map((n) => {
          const { w, h } = nodeSize(n);
          return `${Math.round(w)}x${Math.round(h)}`;
        })
        .join("|") +
      "#" +
      edges
        .map((e) => {
          const d = e.data as any;
          return `${d?.viewType}:${d?.edgeLabelSize}:${d?.showLabelEnlace ? 1 : 0}`;
        })
        .join("|"),
    [nodes, edges],
  );
  const prevSizeKeyRef = useRef(sizeKey);
  useEffect(() => {
    if (prevSizeKeyRef.current === sizeKey) return;
    prevSizeKeyRef.current = sizeKey;
    if (!preLayoutSnapshot || !lastRunRef.current) return;
    const t = setTimeout(() => handleAutoLayout(...lastRunRef.current!), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizeKey]);

  const handleRevertLayout = () => {
    runIdRef.current++;
    lastRunRef.current = null;
    if (!preLayoutSnapshot) return;
    layoutPositionsRef.current = new Map();
    layoutEdgesRef.current = new Map();
    setNodes((prev) =>
      prev.map((node) => {
        const saved = preLayoutSnapshot.nodes.find((p) => p.id === node.id);
        return saved ? { ...node, position: saved.position } : node;
      }),
    );
    setEdges((prev) =>
      prev.map((edge) => {
        const saved = preLayoutSnapshot.edges.find((e) => e.id === edge.id);
        if (!saved || !edge.data) return edge;
        return {
          ...edge,
          sourceHandle: saved.sourceHandle,
          targetHandle: saved.targetHandle,
          data: {
            ...edge.data,
            positionHandlers: saved.positionHandlers,
            lineType: saved.lineType,
            labelT: saved.labelT,
          },
        };
      }),
    );
    setPreLayoutSnapshot(null);
  };

  return {
    preLayoutSnapshot,
    setPreLayoutSnapshot,
    layoutPositionsRef,
    layoutEdgesRef,
    handleAutoLayout,
    handleRevertLayout,
  };
}
